/**
 * F12 — StageStatusChip (v2).
 * Shared presentational chip for interior execution status.
 * data-testid convention (AC #10):
 *   stage:    data-testid="stage-chip-<stageKey>-<status>"
 *   sub-phase: data-testid="substage-chip-<stageKey>-<subPhase>-<status>"
 * Tone map (inline Tailwind):
 *   VALID               → emerald-600 bg-emerald-50
 *   BLOCKED             → rose-700 bg-rose-50
 *   UPCOMING            → amber-600 bg-amber-50
 *   LAPSED              → orange-600 bg-orange-50
 *   CONFLICT            → rose-900 bg-rose-100
 *   POSSESSION_COMPLETE → slate-500 bg-slate-100
 * Tests key on data-testid, never on class names.
 */
import React from "react";

/** @type {Record<string, {text: string, bg: string}>} */
const TONE = {
  VALID:               { text: "text-emerald-600", bg: "bg-emerald-50" },
  BLOCKED:             { text: "text-rose-700",    bg: "bg-rose-50" },
  UPCOMING:            { text: "text-amber-600",   bg: "bg-amber-50" },
  LAPSED:              { text: "text-orange-600",  bg: "bg-orange-50" },
  CONFLICT:            { text: "text-rose-900",    bg: "bg-rose-100" },
  POSSESSION_COMPLETE: { text: "text-slate-500",   bg: "bg-slate-100" },
};

const LABEL = {
  VALID:               "Valid",
  BLOCKED:             "Blocked",
  UPCOMING:            "Upcoming",
  LAPSED:              "Lapsed",
  CONFLICT:            "Conflict",
  POSSESSION_COMPLETE: "Complete",
};

/**
 * @param {object} props
 * @param {string} props.status       One of the 6 contract status strings.
 * @param {string} props.stageKey     Machine key for the stage (typeName / stageKey).
 * @param {string} [props.subPhase]   Sub-phase key; if set renders substage-chip-* testid.
 * @param {string} [props.className]  Extra Tailwind classes.
 */
const StageStatusChip = ({ status, stageKey, subPhase, className = "" }) => {
  const tone = TONE[status] || { text: "text-slate-500", bg: "bg-slate-100" };
  const label = LABEL[status] || status;

  const testId = subPhase
    ? `substage-chip-${stageKey}-${subPhase}-${status}`
    : `stage-chip-${stageKey}-${status}`;

  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tone.bg} ${tone.text} ${className}`}
    >
      {label}
    </span>
  );
};

export default StageStatusChip;
